import { Link } from "wouter";
import { Render } from "../render/Render";
import { useSession } from "../lib/session";

/**
 * Orders — the observed buyer.zeemart.co flow has THREE sub-tabs under
 * /orders: "all", "placed", "pendingApproval". The actual list endpoint
 * (po/bulk family) requires an `orderDataKey` parameter that the
 * original SPA sources from a user-pref endpoint we haven't yet traced.
 *
 * Until that's traced, the page renders the sub-tab shell with an
 * honest "endpoint trace required" empty state — not a fake list.
 */
type Tab = "all" | "placed" | "pending";

const TABS: Array<{ key: Tab; label: string; href: string }> = [
  { key: "all",     label: "All",     href: "/app/orders" },
  { key: "placed",  label: "Placed",  href: "/app/orders/placed" },
  { key: "pending", label: "Pending approval", href: "/app/orders/pendingApproval" },
];

export function Orders({ subtab = "all" }: { subtab?: Tab }) {
  const { session } = useSession();

  type Elem = { type: string; props: Record<string, unknown>; children: string[] };
  const elements: Record<string, Elem> = {
    root: {
      type: "Page",
      props: {
        title: "Orders",
        subtitle: session.activeOutletId ? `Outlet ${session.activeOutletId}` : null,
      },
      children: ["body"],
    },
    body: { type: "Section", props: { title: null, density: "regular" }, children: ["content"] },
    content: {
      type: "EmptyState",
      props: {
        title: "Orders list — endpoint trace required",
        body:
          "The original /orders screen calls /services/orders/po/bulk with an orderDataKey parameter sourced from a user-pref bootstrap call. Re-run observe-original.sh against an account WITH orders to capture the full request shape (start/end window, filter mode, paging), then wire it here.",
      },
      children: [],
    },
  };

  return (
    <div>
      <div className="px-8 pt-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-1 mb-2 text-xs">
          {TABS.map((t) => (
            <Link
              key={t.key}
              href={t.href}
              className={`px-3 py-1.5 rounded-full transition ${
                t.key === subtab
                  ? "bg-nomni-deep text-white"
                  : "text-ink-2 hover:bg-rule/60"
              }`}
            >
              {t.label}
            </Link>
          ))}
        </div>
      </div>
      <Render spec={{ root: "root", elements } as never} />
    </div>
  );
}
